import { TagVariant } from '../../components/AdminTag';
import { getStatusType } from '../../components/AdminStatusBadge';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface Location {
  id: string;
  sap: string;
  name: string;
  facilityType: string;
  type: string;
  org: string;
  tpl: string;
  size: string;
  region: string;
  market: string;
  marketLead: string;
  regionLead: string;
  coe: string;
  coe2: string;
  status: string;
  empCount: number;
  isDuo: boolean;
  executionSystem: string;
  // EDF sync flag — governs field locking in Edit mode
  edfSynced?: boolean;
  // Address & Geography (#306064)
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  latitude?: string;
  longitude?: string;
  ownership?: string;
  yearBuilt?: string;
  // Systems & Labor (#306064)
  warehouseSystem?: string;
  warehouseStartTime?: string;
  labor?: string;
  union?: string;
  supplier?: string;
  // Capacity & Scale (#306064)
  totalSqft?: string;
  operationalSqft?: string;
  pickedVolume?: string;
  salesVolume?: string;
  productionVolume?: string;
  // Docks & Production (#306064)
  docks?: string;
  docksEng?: string;
  docksNoEng?: string;
  driveIn?: string;
  bottleLines?: string;
  canLines?: string;
  // Network Relationships (#306064)
  build1?: string;
  build2?: string;
  bulkSite?: string;
}

export interface FilterState {
  search: string;
  type: string[];
  facilityType: string[];
  org: string[];
  tpl: string[];
  unionStatus: string[];
  region: string[];
  market: string[];
  locationNames: string[];
  size: string[];
  status: string[];
}

/** Full form values used by Add, Edit, and Bulk Edit modes */
export interface LocationFormValues {
  // Primary Information
  id: string;
  sap: string;
  name: string;
  status: string;
  // Facility Classification
  facilityType: string;
  type: string;   // department
  org: string;
  tpl: string;
  size: string;
  ownership: string;
  yearBuilt: string;
  // Address & Geography
  address: string;
  city: string;
  state: string;
  zip: string;
  latitude: string;
  longitude: string;
  region: string;
  market: string;
  // Resource Assignment
  marketLead: string;
  regionLead: string;
  coe: string;
  coe2: string;
  // Systems & Labor
  executionSystem: string;
  warehouseSystem: string;
  labor: string;
  union: string;
  supplier: string;
  // Capacity & Scale
  totalSqft: string;
  operationalSqft: string;
  pickedVolume: string;
  // Docks & Production
  docks: string;
  docksEng: string;
  docksNoEng: string;
  driveIn: string;
  bottleLines: string;
  canLines: string;
  // Network Relationships
  build1: string;
  build2: string;
  bulkSite: string;
}

/** Field-level audit record for bulk and individual edits */
export interface LocationAuditEntry {
  locationId: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  changedAt: string;
  changedBy?: string;
}

export interface PagedResult {
  items: Location[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────
export const SESSION_KEY = 'lms_session_v1';

export const STATUS_ORDER: Record<string, number> = {
  'Active': 0, 'Pending Activation': 1, 'Marked for Removal': 2, 'Disabled': 3,
};

export const DEFAULT_FILTERS: FilterState = {
  search: '', type: [], facilityType: [], org: [], tpl: [], unionStatus: [], region: [], market: [], locationNames: [], size: [], status: [],
};

export const LOCATION_FACILITY_TYPE_OPTIONS = [
  'Cross Dock',
  'Drop Lot',
  'OSW',
  'Production - Combo',
  'Production - Transport Only',
  'Satellite Warehouse',
];

function normalizeUnionStatus(value: string | undefined): string {
  const text = (value ?? '').trim().toLowerCase();
  if (!text) return 'Unknown';
  if (text === 'union' || text === 'yes' || text === 'y' || text === 'true') return 'Union';
  if (text === 'non-union' || text === 'non union' || text === 'no' || text === 'n' || text === 'false') return 'Non-Union';
  return 'Unknown';
}

// ─── Session storage helper (web-safe) ───────────────────────────────────────
export const sessionStore = {
  get: (key: string): Promise<string | null> => {
    try {
      if (typeof sessionStorage !== 'undefined') {
        return Promise.resolve(sessionStorage.getItem(key));
      }
    } catch { /* ignore */ }
    return Promise.resolve(null);
  },
  set: (key: string, value: string): Promise<void> => {
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(key, value);
      }
    } catch { /* ignore */ }
    return Promise.resolve();
  },
};

// ─── Pure logic helpers ───────────────────────────────────────────────────────
export function defaultSort(a: Location, b: Location): number {
  const diff = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
  if (diff !== 0) return diff;
  return a.name.localeCompare(b.name);
}

export function applyFilters(data: Location[], f: FilterState): Location[] {
  return data.filter(l => {
    if (f.search) {
      const s = f.search.toLowerCase();
      if (!l.id.toLowerCase().includes(s) &&
          !l.sap.toLowerCase().includes(s) &&
          !l.name.toLowerCase().includes(s) &&
          !l.facilityType.toLowerCase().includes(s) &&
          !l.type.toLowerCase().includes(s) &&
          !l.org.toLowerCase().includes(s) &&
          !l.tpl.toLowerCase().includes(s) &&
          !l.size.toLowerCase().includes(s) &&
          !l.region.toLowerCase().includes(s) &&
          !l.market.toLowerCase().includes(s) &&
          !l.coe.toLowerCase().includes(s) &&
          !l.coe2.toLowerCase().includes(s) &&
          !l.status.toLowerCase().includes(s)) return false;
    }
    if (f.type.length   > 0 && !f.type.includes(l.type))     return false;
    if (f.facilityType.length > 0 && !f.facilityType.includes(l.facilityType)) return false;
    if (f.org.length    > 0 && !f.org.includes(l.org))       return false;
    if (f.tpl.length    > 0 && !f.tpl.includes(l.tpl))       return false;
    if (f.unionStatus.length > 0 && !f.unionStatus.includes(normalizeUnionStatus(l.union))) return false;
    if (f.region.length > 0 && !f.region.includes(l.region)) return false;
    if (f.market.length > 0 && !f.market.includes(l.market)) return false;
    if (f.locationNames.length > 0 && !f.locationNames.includes(l.name)) return false;
    if (f.size.length   > 0 && !f.size.includes(l.size))     return false;
    if (f.status.length > 0 && !f.status.includes(l.status)) return false;
    return true;
  });
}

export function statusVariant(status: string): TagVariant {
  const type = getStatusType(status);
  if (type === 'active')  return 'green';
  if (type === 'pending') return 'amber';
  if (type === 'removal') return 'red';
  return 'gray';
}
